import { User,X } from '@ui/components/icons';
import { basicToaster } from '@ui/components/Toast';
import { DEFAULT_CHAIN_ID } from '@ui/constants/chain';
import { Portal } from '@ui/lib/park/portal';
import { Button } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { getIsMobile } from '@ui/utils/getUserAgent';
import { ArrowLeft } from 'lucide-react';
import { ReactNode,useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex } from 'styled-system/jsx';
import { useAccount, useConnect } from 'wagmi';
import { injected } from 'wagmi/connectors';

import { NoSSR } from '../NoSSR';
import { useWalletModal } from './useWalletModal';

const GOOGLE_CONNECTOR_ID = 'io.google.privy.wallet';
const GOOGLE_CONNECTOR_FALLBACK = {
  id: GOOGLE_CONNECTOR_ID,
  name: 'Google',
  icon: '/images/google.svg',
};

const getWalletConnectionErrorMessage = (error: unknown, walletName: string) => {
  const err = error as {
    message?: string;
    name?: string;
    shortMessage?: string;
  };

  if (err.name === 'UserRejectedRequestError') {
    return 'Connection request was rejected.';
  }

  if (err.name === 'ProviderNotFoundError') {
    return `${walletName} was not found. Please make sure the extension is installed and enabled.`;
  }

  return (
    err.shortMessage ||
    err.message ||
    `Failed to connect ${walletName}. Please make sure the wallet is installed and unlocked.`
  );
};

export const WalletModalProvider = ({ children }: { children: ReactNode }) => {
  const { isOpen, close, open } = useWalletModal();
  const { connectors, connectAsync, connect, reset, isPending, variables } = useConnect();
  const { isConnected } = useAccount();
  const [showMore, setShowMore] = useState(false);
  const [isGoogleReconnecting, setIsGoogleReconnecting] = useState(false);
  const [hasAutoConnected, setHasAutoConnected] = useState(false);
  const googleReconnectAttemptedRef = useRef(false);

  const closeModal = () => {
    reset();
    googleReconnectAttemptedRef.current = false;
    setIsGoogleReconnecting(false);
    close();
  }

  const googleConnector = useMemo(
    () => connectors.find(c => c.id === GOOGLE_CONNECTOR_ID),
    [connectors],
  );

  useEffect(() => {
    const handler = async () => {
      googleReconnectAttemptedRef.current = false;
      setIsGoogleReconnecting(true);
      open();
    };
    window.addEventListener("wagmi:google:reconnect", handler);
    
    return () => window.removeEventListener("wagmi:google:reconnect", handler);
  }, [open]);

  useEffect(() => {
    if (hasAutoConnected || isConnected) return;

    if (!connectors.length) return;

    const isMobile = getIsMobile();
    // @ts-ignore
    const hasInjected = typeof window !== 'undefined' && !!window.ethereum;

    if (isMobile && hasInjected) {
      connect({ connector: injected(), chainId: DEFAULT_CHAIN_ID });
    }
    setHasAutoConnected(true);
  }, [isConnected, connectors, hasAutoConnected, connect]);

  useEffect(() => {
    if (isConnected) {
      close();
      googleReconnectAttemptedRef.current = false;
      setIsGoogleReconnecting(false);
    }
  }, [isConnected, close]);

  useEffect(() => {
    if (
      !isGoogleReconnecting ||
      !googleConnector ||
      isConnected ||
      googleReconnectAttemptedRef.current
    ) {
      return;
    }

    googleReconnectAttemptedRef.current = true;
    connectAsync({
      connector: googleConnector,
      chainId: DEFAULT_CHAIN_ID,
    }).catch((error) => {
      googleReconnectAttemptedRef.current = false;
      setIsGoogleReconnecting(false);

      const message = getWalletConnectionErrorMessage(error, 'Google');

      console.error('[WalletModal] Failed to reconnect Google wallet:', {
        connectorId: googleConnector.id,
        connectorName: googleConnector.name,
        error,
      });
      basicToaster.error({
        title: 'Unable to connect Google',
        description: message,
      });
    });
  }, [
    connectAsync,
    googleConnector,
    isConnected,
    isGoogleReconnecting,
  ]);

  // Reset view when closed
  useEffect(() => {
    if (!isOpen) {
      setShowMore(false);
    }
  }, [isOpen]);

  const [lastUsedId, setLastUsedId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      const id = localStorage.getItem('lastUsedConnectorId');
      setLastUsedId(id);
    }
  }, [isOpen]);

  const handleConnect = async (connector: any) => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('lastUsedConnectorId', connector.id);
        setLastUsedId(connector.id);
      }

      // Check if connector is installed (if property exists)
      // We check if 'installed' property exists and is false.
      const isInstalled = connector.installed !== false;

      if (!isInstalled && connector.downloadUrls?.download) {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(
          window.navigator.userAgent
        );

        const downloadUrl = connector.downloadUrls.download;

        if (isMobile) {
          window.open(downloadUrl, '_blank');

return;
        } else {
          window.open(downloadUrl, '_blank');
          
return;
        }
      }

      await connectAsync({ 
        connector,
        chainId: DEFAULT_CHAIN_ID
      });
    } catch (error) {
      const message = getWalletConnectionErrorMessage(
        error,
        connector.name || 'wallet',
      );

      console.error('[WalletModal] Failed to connect wallet:', {
        connectorId: connector.id,
        connectorName: connector.name,
        error,
      });
      basicToaster.error({
        title: `Unable to connect ${connector.name || 'wallet'}`,
        description: message,
      });
    }
  };

  const { mainConnectors, moreConnectors } = useMemo(() => {
    // Filter out WalletConnect from the main list
    let all = connectors.filter(c => !c.disabled);

    // 1. De-duplicate connectors by ID
    const seenIds = new Set();
    all = all.filter(c => {
      if (seenIds.has(c.id)) return false;
      seenIds.add(c.id);
      
return true;
    });
    // Helper to check if a connector is "installed"
    const isInstalled = (c: any) => c.installed !== false;

    // Helper to check if a connector is "suggested"
    // Checking custom property 'suggested' or fallback to our manual list
    const isSuggested = (c: any) => c.suggested == true;

    // 3. Sort logic:
    // - Last used (always first)
    // - Installed wallets
    // - Suggested wallets (even if not installed)
    // - Others (not installed, not suggested)
    
    all = all.sort((a: any, b: any) => {
      // 1. Last used priority
      if (lastUsedId) {
        if (a.id === lastUsedId) return -1;
        if (b.id === lastUsedId) return 1;
      }
      
      const aInstalled = isInstalled(a);
      const bInstalled = isInstalled(b);
      const aSuggested = isSuggested(a);
      const bSuggested = isSuggested(b);

      // 2. Installed wallets first
      if (aInstalled && !bInstalled) return -1;
      if (!aInstalled && bInstalled) return 1;

      // 3. Suggested wallets next (among non-installed ones, or if both installed)
      if (aSuggested && !bSuggested) return -1;
      if (!aSuggested && bSuggested) return 1;

      return 0;
    });

    const limit = 4; // Show 3 items + "More options" button = 4 slots total if there are many

    if (all.length <= 4) {
      return { mainConnectors: all, moreConnectors: [] };
    }

    return {
      mainConnectors: all.slice(0, limit),
      moreConnectors: all.slice(limit)
    };
  }, [connectors, lastUsedId]);

  const ConnectorButton = ({ connector }: { connector: any }) => (
    <Button
      key={connector.uid}
      onClick={() => handleConnect(connector)}
      justifyContent="space-between"
      h="56px"
      p="10px 12px"
      w="full"
      bg="rgba(255,255,255,0)"
      cursor="pointer"
      _hover={{ bg: 'rgba(255,255,255,0.06)', transform: 'scale(1.01)' }}
      _active={{ transform: 'scale(0.99)' }}
      disabled={isPending}
      transition="all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)"
      border="1px solid rgba(255,255,255,0.05)"
    >
      <Flex alignItems="center" gap="16px">
        <Box
          borderRadius="10px"
          overflow="hidden"
          w="40px"
          h="40px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <img
            src={connector.icon}
            alt={connector.name}
            style={{ width: '24px', height: '24px', objectFit: 'contain' }}
          />
        </Box>
        <Text
          fontSize="14px"
          fontWeight="normal"
          color="white"
          letterSpacing="-0.01em"
        >
          {connector.name}
        </Text>
      </Flex>
      {/* Recent tag if it's the last used wallet */}
      {connector.id === lastUsedId && !showMore && (
        <Box
          bg="rgba(255, 255, 255, 0.1)"
          color="white"
          px="10px"
          py="5px"
          borderRadius="8px"
          mr={!isPending && connector.installed === false ? 2 : 0}
        >
          <Text fontSize="12px" fontWeight="normal" letterSpacing="0.02em">
            RECENT
          </Text>
        </Box>
      )}
      {!isPending &&
        connector.installed === false &&
        typeof window !== 'undefined' &&
        !/iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent) && (
          <Box
            bg="rgba(56, 134, 255, 0.15)"
            color="#3886FF"
            px="10px"
            py="5px"
            borderRadius="8px"
          >
            <Text fontSize="12px" fontWeight="normal" letterSpacing="0.02em">
              INSTALL
            </Text>
          </Box>
        )}
    </Button>
  );

  const pendingConnector = variables?.connector;

  const activeLoadingConnector = (isPending && pendingConnector) ? pendingConnector : (isGoogleReconnecting ? googleConnector ?? GOOGLE_CONNECTOR_FALLBACK : null);
  const isLoading = !!activeLoadingConnector;

  return (
    <>
      {children}
      <Dialog.Root lazyMount unmountOnExit open={isOpen} onOpenChange={(e) => !e.open && closeModal()}>
        <Portal>
          <Dialog.Backdrop
            backdropFilter="blur(6px)"
            bg="rgba(0,0,0,0.001)"
            zIndex={2000}
          />
          <Dialog.Positioner
            zIndex={2001}
            alignItems={{ base: 'flex-end', md: 'center' }}
            justifyContent="center"
            p={{ base: 0, md: 4 }}
          >
            <Dialog.Content
              bg="#111"
              color="white"
              w={{ base: '100%', md: '380px' }}
              maxW={{ base: '100%', md: '90vw' }}
              maxH="min-content"
              pointerEvents="none"
              borderRadius={{ base: '16 16 0 0', md: '16' }}
              overflow="hidden"
              border="none"
              boxShadow="none"
              animationDuration="0.2s"
            >
              {isLoading && activeLoadingConnector ? (
                <Flex
                  flexDir="column"
                  alignItems="center"
                  justifyContent="center"
                  pt="60px"
                  pb="40px"
                  px="24px"
                  gap="24px"
                >
                  <Dialog.CloseTrigger asChild>
                    <Button
                      aria-label="Close"
                      variant="ghost"
                      p="0"
                      minW="32px"
                      h="32px"
                      borderRadius="4"
                      bg="rgba(255,255,255,0.05)"
                      _hover={{ bg: 'rgba(255,255,255,0.1)' }}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      cursor="pointer"
                      position="absolute"
                      right="16px"
                      top="16px"
                    >
                      <Icon boxSize="4" color="#666">
                        <X />
                      </Icon>
                    </Button>
                  </Dialog.CloseTrigger>

                  <Box position="relative" w="80px" h="80px" display="flex" alignItems="center" justifyContent="center">
                    {/* Spinner Ring */}
                    <Box
                      position="absolute"
                      top="0"
                      left="0"
                      right="0"
                      bottom="0"
                      borderRadius="full"
                      border="3px solid rgba(255,255,255,0.1)"
                      borderTopColor="#FFF"
                      style={{
                        animation: 'spin 1s linear infinite',
                      }}
                    />

                    {/* Connector Icon */}
                    <Box
                      overflow="hidden"
                      w="48px"
                      h="48px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {/* @ts-ignore */}
                      {activeLoadingConnector?.icon ? (
                        <img
                          // @ts-ignore
                          src={activeLoadingConnector.icon}
                          alt={activeLoadingConnector.name}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <img
                          // @ts-ignore
                          src={googleConnector.icon}
                          alt="Google"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Flex flexDir="column" alignItems="center" gap="8px" textAlign="center">
                    <Text fontSize="18px" fontWeight="600" color="white">
                      Waiting for {activeLoadingConnector?.name}
                    </Text>
                    <Text fontSize="14px" color="#888" lineHeight="1.5" maxW="260px">
                      Don&apos;t see your wallet? Check your other browser windows.
                    </Text>
                  </Flex>
                </Flex>
              ) : (
                <>
                  <Dialog.Title asChild>
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                      pt="50px"
                      pb="16px"
                      flexDir="column"
                      gap="12px"
                    >
                      <img src="/images/logo.svg" alt="LeverUp" style={{
                        maxHeight: "90px",
                        marginTop: "20px"
                      }} />
                      <Dialog.CloseTrigger asChild>
                        <Button
                          aria-label="Close"
                          variant="ghost"
                          p="0"
                          minW="32px"
                          h="32px"
                          borderRadius="4"
                          bg="rgba(255,255,255,0.05)"
                          _hover={{ bg: 'rgba(255,255,255,0.1)' }}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          cursor="pointer"
                          position="absolute"
                          right="16px"
                          top="16px"
                        >
                          <Icon boxSize="4" color="#666">
                            <X />
                          </Icon>
                        </Button>
                      </Dialog.CloseTrigger>
                    </Flex>
                  </Dialog.Title>
                  <Box h="40px" />
                  <Dialog.Description maxH="400px" overflowY="scroll" p="0">
                    <NoSSR>
                      <Flex flexDir="column" px="20px" pb="24px" gap="8px">
                        {!showMore ? (
                          <>
                            {mainConnectors.map((connector) => (
                              <ConnectorButton key={connector.uid} connector={connector} />
                            ))}
                            {moreConnectors.length > 0 && (
                              <Button
                                onClick={() => setShowMore(true)}
                                justifyContent="space-between"
                                h="58px"
                                p="10px 12px"
                                w="full"
                                bg="rgba(255,255,255,0)"
                                cursor="pointer"
                                _hover={{ bg: 'rgba(255,255,255,0.06)', transform: 'scale(1.01)' }}
                                _active={{ transform: 'scale(0.99)' }}
                                transition="all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)"
                                border="1px solid rgba(255,255,255,0.05)"
                              >
                                <Flex alignItems="center" gap="16px">
                                  <Box
                                    overflow="hidden"
                                    w="40px"
                                    h="40px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <Box
                                      w="24px"
                                      h="24px"
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      bg="rgba(255,255,255,0.05)"
                                    >
                                      <Icon boxSize="5" color="white">
                                        <User />
                                      </Icon>
                                    </Box>
                                  </Box>
                                  <Text
                                    fontSize="14px"
                                    fontWeight="normal"
                                    color="white"
                                    letterSpacing="-0.01em"
                                  >
                                    More options
                                  </Text>
                                </Flex>
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            {moreConnectors.map((connector) => (
                              <ConnectorButton key={connector.uid} connector={connector} />
                            ))}

                            <Button
                              variant="ghost"
                              p="0"
                              w="32px"
                              h="32px"
                              borderRadius="4"
                              bg="rgba(255,255,255,0.05)"
                              _hover={{ bg: 'rgba(255,255,255,0.1)' }}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              cursor="pointer"
                              position="absolute"
                              left="16px"
                              top="16px"
                              onClick={() => setShowMore(false)}
                            >
                              <Icon boxSize="4" color="#666">
                                <ArrowLeft />
                              </Icon>
                            </Button>
                          </>
                        )}
                      </Flex>

                      {/* Optional footer */}
                      {!showMore && (
                        <Box px="24px" pb="24px" pt="0">
                          <Text fontSize="12px" color="#6B7280" textAlign="center" lineHeight="1.5">
                            By connecting, you agree to the <span style={{ color: '#9CA3AF', cursor: 'pointer' }}>Terms</span> & <span style={{ color: '#9CA3AF', cursor: 'pointer' }}>Privacy Policy</span>
                          </Text>
                        </Box>
                      )}
                    </NoSSR>
                  </Dialog.Description>
                </>
              )}
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
};